import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Download, FileText, Users, Settings2, ArrowRight, PenLine, Upload, Image as ImageIcon, Send } from "lucide-react";
import api from "@/lib/api";
import SignaturePadModal from "../components/SignaturePadModal";
import BillingTracker from "../components/billing/BillingTracker";
import SendEmailModal from "../components/billing/SendEmailModal";
import RemindersBar from "../components/billing/RemindersBar";
import { eur, KIND_LABEL, INVOICE_STATUS } from "@/lib/format";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const NEW_QUOTE = () => ({
  titre: "", client_id: "", chantier: { adresse: "", cp: "", ville: "", surface: "" },
  date_creation: new Date().toISOString(), validite_jours: 30, acompte_pct: 30,
  remise_globale_pct: 0, notes_client: "", conditions_reglement: "Acompte 30 % à la commande, solde à réception des travaux.",
  sections: [{ titre: "Peinture", description: "", items: [{ kind: "prestation", nom: "Préparation et peinture murs", description: "Protection + rebouchage + enduit + ponçage + 2 couches", unite: "m²", quantite: 60, prix_unitaire: 32, tva: 10 }] }],
});

export default function DocumentsPanel({ projectId = null, project = null, onlyTabs = null, title = "Documents commerciaux" }) {
  const [tab, setTab] = useState(onlyTabs ? onlyTabs[0] : "devis");
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [projectsList, setProjectsList] = useState([]);
  const [settings, setSettings] = useState(null);
  const [editing, setEditingRaw] = useState(null);
  const setEditing = (v) => { setEditingRaw(v); if (v === null || (v && !v.__keepScroll && (v.id !== (editing && editing.id) || !editing))) window.scrollTo({ top: 0, behavior: "instant" }); }; // quote being edited
  const [clientForm, setClientForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [signing, setSigning] = useState(false);
  const [sending, setSending] = useState(null); // {docType, doc}
  const [logoPreview, setLogoPreview] = useState("");
  const logoInputRef = useRef(null);

  // Affiche « raison sociale » pour un pro, sinon « prénom nom »
  const clientLabel = (c) => c ? (c.raison_sociale || `${c.prenom || ""} ${c.nom || ""}`.trim() || "—") : "—";
  const clientLabelById = (id) => clientLabel(clients.find(c => c.id === id));

  const load = async () => {
    setLoading(true); setLoadError(null);
    try {
      const [q, i, c, s, logo] = await Promise.all([
        api.get("/docs/quotes"), api.get("/docs/invoices"),
        api.get("/docs/clients"), api.get("/docs/settings"),
        api.get("/docs/settings/logo"),
      ]);
      setQuotes(q.data.items); setInvoices(i.data.items);
      setClients(c.data.items); setSettings(s.data);
      setLogoPreview(logo.data.logo || "");
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || e?.message || "Impossible de charger les documents.";
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); api.get("/chantiers").then(r => setProjectsList(r.data.items)).catch(() => {}); }, []);

  // ============ Client editor ============
  const saveClient = async () => {
    if (!clientForm.nom && !clientForm.raison_sociale) { toast.error("Nom ou raison sociale requis"); return; }
    try {
      if (clientForm.id) await api.put(`/docs/clients/${clientForm.id}`, clientForm);
      else await api.post("/docs/clients", clientForm);
      toast.success("Client enregistré"); setClientForm(null); load();
    } catch { toast.error("Erreur"); }
  };
  const deleteClient = async (id) => {
    if (!confirm("Supprimer ce client ?")) return;
    await api.delete(`/docs/clients/${id}`); load();
  };

  // ============ Quote editor ============
  const openNew = async () => {
    const base = { ...NEW_QUOTE() };
    if (projectId && projectId !== "__none__" && project) {
      base.project_id = projectId;
      base.titre = project.type_projet || "";
      base.chantier = { ...base.chantier, adresse: project.contact?.adresse || "", cp: project.contact?.cp || "", ville: project.contact?.ville || project.ville || "", surface: project.surface ? String(project.surface) : "" };
      const email = project.contact?.email;
      let client = clients.find(c => email && c.email === email);
      if (!client && project.contact?.nom) {
        try {
          const { data } = await api.post("/docs/clients", { type: "particulier", nom: project.contact.nom, prenom: project.contact.prenom || "", email: email || "", telephone: project.contact.telephone || "", adresse: "", cp: "", ville: "" });
          client = data; setClients([...clients, data]);
        } catch {}
      }
      if (client) base.client_id = client.id;
    }
    setEditing(base);
  };
  const openQuote = async (id) => {
    const { data } = await api.get(`/docs/quotes/${id}`);
    setEditing(data);
  };
  const saveQuote = async () => {
    try {
      const payload = { ...editing };
      if (editing.id) {
        const { data } = await api.put(`/docs/quotes/${editing.id}`, payload);
        setEditing(data); toast.success("Devis enregistré");
      } else {
        const { data } = await api.post("/docs/quotes", payload);
        setEditing(data); toast.success(`Devis ${data.numero} créé`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const setStatus = async (id, status) => {
    await api.post(`/docs/quotes/${id}/status`, { status });
    toast.success("Statut mis à jour"); load();
    if (editing?.id === id) setEditing({ ...editing, status });
  };
  const convertToInvoice = async (id) => {
    // Détermine si un acompte existe déjà pour ce devis → on créera une facture de solde
    const hasAcompte = invoices.some(f => f.quote_id === id && f.kind === "acompte");
    const msg = hasAcompte
      ? "Émettre la facture de SOLDE ? L'acompte déjà facturé sera automatiquement déduit."
      : "Convertir ce devis en facture ? Le devis passera au statut « Accepté ».";
    if (!confirm(msg)) return;
    try {
      const { data } = await api.post(`/docs/quotes/${id}/convert`);
      const label = data.kind === "solde" ? "Facture de solde" : "Facture";
      toast.success(`${label} ${data.numero} créée`);
      setEditing(null); load(); setTab("factures");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const submitSignature = async (dataUrl, signedBy) => {
    if (!editing?.id) return;
    try {
      const { data } = await api.post(`/docs/quotes/${editing.id}/sign`, { signature: dataUrl, signed_by: signedBy });
      setEditing({ ...editing, signature: data.signature, signed_at: data.signed_at, signed_by: data.signed_by, status: "accepte" });
      const ai = data.acompte_invoice;
      if (ai?.numero) {
        toast.success(`Devis signé · Facture d'acompte ${ai.numero} générée (${ai.totals?.total_ttc?.toLocaleString("fr-FR")} € TTC)`);
      } else {
        toast.success("Devis signé — statut mis à jour");
      }
      setSigning(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur signature"); }
  };
  const removeSignature = async () => {
    if (!editing?.id) return;
    if (!confirm("Supprimer la signature du devis ?")) return;
    try {
      await api.delete(`/docs/quotes/${editing.id}/sign`);
      setEditing({ ...editing, signature: null, signed_at: null, signed_by: "", status: "envoye" });
      toast.success("Signature supprimée"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const onLogoFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!/^image\/(png|jpeg|jpg)$/i.test(file.type)) { toast.error("PNG ou JPG uniquement"); return; }
    if (file.size > 4 * 1024 * 1024) { toast.error("Fichier trop lourd (max 4 Mo)"); return; }
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
    });
    try {
      const { data } = await api.post("/docs/settings/logo", { logo: dataUrl });
      setLogoPreview(data.logo);
      toast.success("Logo mis à jour — appliqué aux prochains PDF");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload impossible"); }
    finally { if (logoInputRef.current) logoInputRef.current.value = ""; }
  };
  const resetLogo = async () => {
    if (!confirm("Restaurer le logo par défaut RAMA DÉCO ?")) return;
    try { await api.delete("/docs/settings/logo"); const { data } = await api.get("/docs/settings/logo"); setLogoPreview(data.logo || ""); toast.success("Logo réinitialisé"); }
    catch { toast.error("Erreur"); }
  };
  const openPdf = async (kind, id) => {
    try {
      const url = `${API_BASE}/api/docs/${kind === "devis" ? "quotes" : "invoices"}/${id}/pdf`;
      const token = localStorage.getItem("rama_token");
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await r.blob();
      const bu = URL.createObjectURL(blob);
      window.open(bu, "_blank");
    } catch { toast.error("Erreur PDF"); }
  };

  // ============ Section / item helpers (editor) ============
  const addSection = () => setEditing({ ...editing, sections: [...editing.sections, { titre: "Nouvelle section", items: [] }] });
  const updSection = (i, key, val) => { const s = [...editing.sections]; s[i] = { ...s[i], [key]: val }; setEditing({...editing, sections: s}); };
  const delSection = (i) => setEditing({ ...editing, sections: editing.sections.filter((_,k)=>k!==i) });
  const addItem = (si) => { const s = [...editing.sections]; s[si].items = [...(s[si].items||[]), { kind: "prestation", nom: "", unite: "u", quantite: 1, prix_unitaire: 0, tva: 10 }]; setEditing({...editing, sections: s}); };
  const updItem = (si, ii, key, val) => { const s = [...editing.sections]; const it = { ...s[si].items[ii], [key]: val }; s[si].items = s[si].items.map((x,k)=>k===ii?it:x); setEditing({...editing, sections: s}); };
  const delItem = (si, ii) => { const s = [...editing.sections]; s[si].items = s[si].items.filter((_,k)=>k!==ii); setEditing({...editing, sections: s}); };

  const STATUS_LABEL = { brouillon: "Brouillon", envoye: "Envoyé", accepte: "Accepté", refuse: "Refusé", expire: "Expiré", archive: "Archivé", emise: "Émise", ...INVOICE_STATUS };
  const hasBilling = (qid) => invoices.some(f => f.quote_id === qid && f.kind !== "acompte");
  const visibleQuotes = projectId === "__none__" ? quotes.filter(q => !q.project_id) : projectId ? quotes.filter(q => q.project_id === projectId) : quotes;
  const visibleQuoteIds = new Set(visibleQuotes.map(q => q.id));
  const visibleInvoices = projectId ? invoices.filter(f => visibleQuoteIds.has(f.quote_id)) : invoices;
  const subtabs = [
    {v:"devis", l:"Devis", i:FileText, c:visibleQuotes.length},
    {v:"factures", l:"Factures", i:FileText, c:visibleInvoices.length},
    {v:"clients", l:"Clients", i:Users, c:clients.length},
    {v:"parametres", l:"Paramètres", i:Settings2},
  ].filter(t => onlyTabs ? onlyTabs.includes(t.v) : (!projectId || ["devis", "factures"].includes(t.v)));

  // ==================================================
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        {!projectId ? <h1 className="font-display text-3xl md:text-4xl text-anthracite">{title}</h1> : <div className="font-mono-caption text-ink-soft">Devis & factures du projet</div>}
        <div className="flex gap-2">
          {tab === "devis" && <button data-testid="new-quote-btn" onClick={openNew} className="btn-primary text-sm"><Plus size={16} /> Nouveau devis</button>}
          {tab === "clients" && <button onClick={() => setClientForm({ type:"particulier", nom:"", prenom:"", raison_sociale:"", email:"", telephone:"", adresse:"", cp:"", ville:"" })} data-testid="new-client-btn" className="btn-primary text-sm"><Plus size={16} /> Nouveau client</button>}
        </div>
      </div>

      <div className="flex gap-1 border-b border-black/10 mb-6 overflow-x-auto">
        {subtabs.map(t => (
          <button key={t.v} data-testid={`docs-subtab-${t.v}`} onClick={()=>setTab(t.v)} className={`px-4 py-2 text-sm inline-flex items-center gap-2 whitespace-nowrap border-b-2 -mb-px ${tab===t.v?"border-bordeaux-700 text-bordeaux-700":"border-transparent text-ink-soft hover:text-anthracite"}`}>
            <t.i size={14} /> {t.l} {t.c !== undefined && <span className="text-xs opacity-70">({t.c})</span>}
          </button>
        ))}
      </div>

      {loadError && (
        <div data-testid="docs-load-error" className="mb-6 p-4 border border-bordeaux-500/40 bg-bordeaux-500/5 text-bordeaux-700 rounded-rama-md text-sm flex items-center justify-between">
          <span>Erreur de chargement : {loadError}</span>
          <button onClick={load} className="text-bordeaux-700 underline text-sm ml-4">Réessayer</button>
        </div>
      )}
      {loading && !loadError && (
        <div data-testid="docs-loading" className="mb-6 text-ink-soft text-sm">Chargement…</div>
      )}

      {/* ============ DEVIS LIST ============ */}
      {tab === "devis" && !editing && (
        <div className="md:hidden space-y-2" data-testid="quotes-cards">
          {visibleQuotes.map(q => (
            <button key={q.id} data-testid={`quote-card-${q.id}`} onClick={()=>openQuote(q.id)} className="w-full text-left bg-white border border-black/10 rounded-rama-md p-4">
              <div className="flex justify-between gap-3"><span className="font-mono text-xs text-bordeaux-700">{q.numero}</span><span className="text-xs px-2 py-0.5 bg-black/5 rounded">{STATUS_LABEL[q.status] || q.status}</span></div>
              <div className="font-medium mt-1 truncate">{q.titre || "—"}</div>
              <div className="flex justify-between text-sm text-ink-soft mt-1"><span className="truncate">{clientLabelById(q.client_id)}</span><span className="font-medium text-anthracite">{eur(q.totals?.total_ttc)}</span></div>
            </button>
          ))}
          {visibleQuotes.length === 0 && <div className="p-8 text-center text-ink-soft text-sm bg-white border border-black/10 rounded-rama-md">Aucun devis.</div>}
        </div>
      )}
      {tab === "devis" && !editing && (
        <div className="hidden md:block bg-white border border-black/10 rounded-rama-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/5 text-ink-soft font-mono-caption"><tr>
              <th className="p-3 text-left">Numéro</th><th className="text-left">Titre</th><th className="text-left">Client</th><th className="text-left">Total TTC</th><th className="text-left">Statut</th><th className="text-left">Créé</th><th></th>
            </tr></thead>
            <tbody>
              {visibleQuotes.map(q => (
                <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-t border-black/5">
                  <td className="p-3 font-mono text-bordeaux-700">{q.numero}</td>
                  <td>{q.titre || "—"}</td>
                  <td>{clientLabelById(q.client_id)}</td>
                  <td>{eur(q.totals?.total_ttc)}</td>
                  <td><span className="text-xs px-2 py-1 bg-black/5 rounded">{STATUS_LABEL[q.status] || q.status}</span></td>
                  <td className="text-xs">{new Date(q.created_at).toLocaleDateString("fr-FR")}</td>
                  <td className="text-right pr-3"><button data-testid={`open-quote-${q.id}`} onClick={()=>openQuote(q.id)} className="text-bordeaux-700 hover:underline text-sm">Ouvrir <ArrowRight size={12} className="inline" /></button></td>
                </tr>
              ))}
              {visibleQuotes.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-ink-soft">Aucun devis. Cliquez sur « Nouveau devis ».</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ============ DEVIS EDITOR ============ */}
      {tab === "devis" && editing && (
        <div className="space-y-6">
          <div className="bg-anthracite text-ivory p-4 md:p-5 rounded-rama-md space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono-caption text-ivory/60">{editing.numero || "Nouveau devis"}{project ? ` · ${project.ref}` : ""}</div>
                <div className="font-display text-xl truncate">{editing.titre || "Sans titre"}</div>
              </div>
              <button onClick={()=>setEditing(null)} data-testid="quote-back" className="px-3 py-2 text-sm rounded bg-white/10 hover:bg-white/20 flex-shrink-0">Retour</button>
            </div>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
              <button data-testid="quote-save" onClick={saveQuote} className="btn-primary text-sm justify-center">Enregistrer</button>
              {editing.id && <>
                <button data-testid="quote-pdf" onClick={()=>openPdf("devis", editing.id)} className="btn-ghost btn-ghost-light text-sm justify-center"><Download size={14} /> PDF</button>
                <button data-testid="quote-sign" onClick={()=>setSigning(true)} className="btn-ghost btn-ghost-light text-sm justify-center"><PenLine size={14} /> {editing.signature ? "Re-signer" : "Signature"}</button>
                <button data-testid="quote-send-email" onClick={()=>setSending({ docType: "devis", doc: editing })} className="btn-ghost btn-ghost-light text-sm justify-center"><Send size={14} /> Envoyer</button>
                <select data-testid="quote-status" value={editing.status} onChange={e=>setStatus(editing.id, e.target.value)} className="text-sm text-anthracite rounded-rama-sm px-3 py-2 bg-ivory">
                  {Object.keys(STATUS_LABEL).filter(s=>!["emise","partielle","payee","annulee"].includes(s)).map(s=><option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
                {!hasBilling(editing.id) && editing.status !== "accepte" && (
                  <button data-testid="quote-convert" onClick={()=>convertToInvoice(editing.id)} className="btn-primary text-sm bg-bordeaux-500 hover:bg-bordeaux-700 justify-center col-span-2">Convertir en facture →</button>
                )}
              </>}
            </div>
          </div>

          {editing.id && editing.status === "accepte" && (
            <BillingTracker quote={editing} openPdf={openPdf} onChanged={load} onSend={(inv) => setSending({ docType: "facture", doc: inv })} />
          )}

          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-rama-md border border-black/10 md:col-span-2 space-y-3">
              <input data-testid="quote-titre" className="w-full p-2 border rounded" placeholder="Titre du devis" value={editing.titre} onChange={e=>setEditing({...editing, titre:e.target.value})} />
              <div className="grid sm:grid-cols-2 gap-2">
                <select data-testid="quote-project" className="p-2 border rounded" value={editing.project_id || ""} onChange={e=>setEditing({...editing, project_id:e.target.value || null})}>
                  <option value="">— Projet / chantier lié —</option>
                  {projectsList.map(p => <option key={p.id} value={p.id}>{p.ref} · {p.contact?.nom || p.type_projet || "Prospect"}</option>)}
                </select>
                <select className="p-2 border rounded" value={editing.client_id || ""} onChange={e=>setEditing({...editing, client_id:e.target.value})}>
                  <option value="">— Choisir un client —</option>
                  {clients.map(c=><option key={c.id} value={c.id}>{clientLabel(c)}</option>)}
                </select>
                <label className="block text-sm">
                  <span className="text-xs text-ink-soft">Validité (jours)</span>
                  <input type="number" className="w-full p-2 border rounded mt-1" placeholder="30" value={editing.validite_jours} onChange={e=>setEditing({...editing, validite_jours:Number(e.target.value)})} />
                </label>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                <input className="p-2 border rounded col-span-2 lg:col-span-1" placeholder="Adresse chantier" value={editing.chantier?.adresse||""} onChange={e=>setEditing({...editing, chantier:{...editing.chantier, adresse:e.target.value}})} />
                <input className="p-2 border rounded" placeholder="CP" value={editing.chantier?.cp||""} onChange={e=>setEditing({...editing, chantier:{...editing.chantier, cp:e.target.value}})} />
                <input className="p-2 border rounded" placeholder="Ville" value={editing.chantier?.ville||""} onChange={e=>setEditing({...editing, chantier:{...editing.chantier, ville:e.target.value}})} />
                <input className="p-2 border rounded" placeholder="Surface" value={editing.chantier?.surface||""} onChange={e=>setEditing({...editing, chantier:{...editing.chantier, surface:e.target.value}})} />
              </div>
            </div>
            <div className="bg-white p-4 rounded-rama-md border border-black/10 space-y-2 text-sm">
              <label className="block">Acompte (%) <input type="number" className="w-full p-2 border rounded" value={editing.acompte_pct} onChange={e=>setEditing({...editing, acompte_pct:Number(e.target.value)})} /></label>
              <label className="block">Remise globale (%) <input type="number" className="w-full p-2 border rounded" value={editing.remise_globale_pct} onChange={e=>setEditing({...editing, remise_globale_pct:Number(e.target.value)})} /></label>
              <textarea className="w-full p-2 border rounded" rows={2} placeholder="Conditions règlement" value={editing.conditions_reglement} onChange={e=>setEditing({...editing, conditions_reglement:e.target.value})} />
            </div>
          </div>

          {editing.sections.map((sec, si) => (
            <div key={si} className="bg-white p-4 rounded-rama-md border border-black/10">
              <div className="flex items-center gap-2 mb-3">
                <input className="flex-1 p-2 border-b font-display text-lg text-anthracite" value={sec.titre} onChange={e=>updSection(si,"titre",e.target.value)} placeholder="Titre section (ex : Peinture, Salle de bains…)" />
                <button onClick={()=>delSection(si)} className="text-bordeaux-700 p-2 hover:bg-black/5 rounded"><Trash2 size={14} /></button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-ink-soft"><tr><th className="text-left p-1">Désignation</th><th className="p-1">Unité</th><th className="p-1">Quantité</th><th className="p-1">P.U. HT</th><th className="p-1">TVA %</th><th></th></tr></thead>
                  <tbody>
                    {(sec.items||[]).map((it, ii) => (
                      <tr key={ii} className="border-t border-black/5">
                        <td className="p-1"><input className="w-full p-1 border-b" placeholder="Nom prestation" value={it.nom} onChange={e=>updItem(si,ii,"nom",e.target.value)} />
                          <input className="w-full p-1 text-xs text-ink-soft mt-1 border-b" placeholder="Description client (facultatif)" value={it.description||""} onChange={e=>updItem(si,ii,"description",e.target.value)} /></td>
                        <td className="p-1"><input className="w-16 p-1 border-b" value={it.unite} onChange={e=>updItem(si,ii,"unite",e.target.value)} /></td>
                        <td className="p-1"><input data-testid={`item-qty-${si}-${ii}`} type="number" step="0.01" className="w-20 p-1 border-b text-right" value={it.quantite} onChange={e=>updItem(si,ii,"quantite",Number(e.target.value))} /></td>
                        <td className="p-1"><input data-testid={`item-pu-${si}-${ii}`} type="number" step="0.01" className="w-24 p-1 border-b text-right" value={it.prix_unitaire} onChange={e=>updItem(si,ii,"prix_unitaire",Number(e.target.value))} /></td>
                        <td className="p-1"><select className="p-1 border-b" value={it.tva} onChange={e=>updItem(si,ii,"tva",Number(e.target.value))}>{[20,10,5.5,0].map(t=><option key={t} value={t}>{t}</option>)}</select></td>
                        <td className="p-1"><button onClick={()=>delItem(si,ii)} className="text-bordeaux-700 p-1"><Trash2 size={12} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={()=>addItem(si)} className="mt-2 text-sm text-bordeaux-700 inline-flex items-center gap-1"><Plus size={14} /> Ajouter une ligne</button>
            </div>
          ))}

          <button onClick={addSection} className="btn-ghost"><Plus size={14} /> Ajouter une section</button>

          {editing.totals && (
            <div className="bg-anthracite text-ivory p-6 rounded-rama-md">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><div className="font-mono-caption text-ivory/60">Total HT</div><div data-testid="totals-ht" className="font-display text-xl md:text-2xl">{eur(editing.totals.total_ht, "always")}</div></div>
                <div><div className="font-mono-caption text-ivory/60">Total TVA</div><div data-testid="totals-tva" className="font-display text-xl md:text-2xl">{eur(editing.totals.total_tva, "always")}</div></div>
                <div className="col-span-2 md:col-span-1"><div className="font-mono-caption text-greige">Total TTC</div><div data-testid="totals-ttc" className="font-display text-2xl md:text-3xl">{eur(editing.totals.total_ttc, "always")}</div></div>
              </div>
            </div>
          )}

          {editing.signature && (
            <div className="bg-white border border-black/10 rounded-rama-md p-6 flex flex-col sm:flex-row items-start gap-6" data-testid="quote-signature-preview">
              <div className="flex-1">
                <div className="font-mono-caption text-xs text-bordeaux-700 tracking-widest">SIGNATURE CLIENT</div>
                <div className="font-display text-lg text-anthracite mt-1">Bon pour accord</div>
                <div className="text-sm text-ink-soft mt-2">
                  {editing.signed_by && <span className="text-anthracite">{editing.signed_by} · </span>}
                  Signé le {editing.signed_at ? new Date(editing.signed_at).toLocaleString("fr-FR") : ""}
                </div>
                <button onClick={removeSignature} data-testid="quote-signature-remove" className="text-bordeaux-700 hover:underline text-xs mt-3">Retirer la signature</button>
              </div>
              <img src={editing.signature} alt="Signature" className="max-h-24 border border-black/10 bg-white p-2 rounded" />
            </div>
          )}
        </div>
      )}

      {/* ============ FACTURES ============ */}
      {tab === "factures" && (<>
        {!projectId && <RemindersBar />}
        <div className="bg-white border border-black/10 rounded-rama-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/5 text-ink-soft font-mono-caption"><tr>
              <th className="p-3 text-left">Numéro</th><th className="text-left">Type</th><th className="text-left">Devis lié</th><th className="text-left">Client</th><th className="text-left">Total TTC</th><th className="text-left">Statut</th><th></th>
            </tr></thead>
            <tbody>
              {visibleInvoices.map(f => {
                const kind = f.kind || "totale";
                const kindLabel = KIND_LABEL[kind] || "Facture";
                const kindClass = { acompte: "bg-bordeaux-500/10 text-bordeaux-700", situation: "bg-bronze/15 text-bronze", solde: "bg-anthracite/10 text-anthracite", totale: "bg-black/5", avoir: "bg-greige text-anthracite" }[kind];
                return (
                <tr key={f.id} data-testid={`invoice-row-${f.id}`} className="border-t border-black/5">
                  <td className="p-3 font-mono text-bordeaux-700">{f.numero}</td>
                  <td><span data-testid={`invoice-kind-${f.id}`} className={`text-xs px-2 py-1 rounded font-mono-caption ${kindClass}`}>{kindLabel}</span></td>
                  <td>{f.quote_numero}</td>
                  <td>{clientLabelById(f.client_id)}</td>
                  <td>{eur(f.totals?.total_ttc)}{f.situation_index ? <span className="text-xs text-ink-soft"> · sit. n°{f.situation_index}</span> : ""}</td>
                  <td><span className={`text-xs px-2 py-1 rounded ${f.status === "payee" ? "bg-green-50 text-green-800" : f.status === "brouillon" ? "bg-bordeaux-light text-bordeaux-700" : "bg-black/5"}`}>{STATUS_LABEL[f.status] || f.status}</span></td>
                  <td className="text-right pr-3 whitespace-nowrap">
                    <button onClick={()=>openPdf("facture", f.id)} className="text-bordeaux-700 hover:underline text-sm mr-3"><Download size={12} className="inline" /> PDF</button>
                    {f.status !== "brouillon" && <button data-testid={`invoice-send-${f.id}`} onClick={()=>setSending({ docType: "facture", doc: f })} className="text-bordeaux-700 hover:underline text-sm"><Send size={12} className="inline" /> Envoyer</button>}
                  </td>
                </tr>);
              })}
              {visibleInvoices.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-ink-soft">Aucune facture. Signez ou convertissez un devis pour en émettre une.</td></tr>}
            </tbody>
          </table>
        </div>
      </>)}

      {/* ============ CLIENTS ============ */}
      {tab === "clients" && (
        <div className="bg-white border border-black/10 rounded-rama-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/5 text-ink-soft font-mono-caption"><tr><th className="p-3 text-left">Nom</th><th className="text-left">Type</th><th className="text-left">Ville</th><th className="text-left">Contact</th><th></th></tr></thead>
            <tbody>
              {clients.map(c => (
                <tr key={c.id} data-testid={`client-row-${c.id}`} className="border-t border-black/5">
                  <td className="p-3 font-medium">{clientLabel(c)}</td>
                  <td>{c.type}</td><td>{c.ville}</td><td className="text-xs">{c.email} · {c.telephone}</td>
                  <td className="text-right pr-3 flex gap-2 justify-end">
                    <button onClick={()=>setClientForm({...c})} className="text-bordeaux-700 hover:underline text-sm">Modifier</button>
                    <button onClick={()=>deleteClient(c.id)} className="text-bordeaux-700"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {clients.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-ink-soft">Aucun client. Cliquez sur « Nouveau client ».</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ============ PARAMÈTRES ============ */}
      {tab === "parametres" && settings && (
        <div className="space-y-6">
          {/* Bloc logo */}
          <div className="bg-white p-6 rounded-rama-md border border-black/10">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-display text-xl text-anthracite">Logo de l'entreprise</h2>
                <p className="text-xs text-ink-soft mt-1">Apparaît en haut de chaque devis et facture. PNG ou JPG · max 4 Mo · fond transparent conseillé.</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="w-64 h-24 border border-dashed border-black/20 rounded flex items-center justify-center bg-[#F9F6F0] p-3">
                {logoPreview
                  ? <img src={logoPreview} alt="Logo actuel" className="max-h-full max-w-full object-contain" data-testid="logo-preview" />
                  : <span className="text-xs text-ink-soft flex items-center gap-2"><ImageIcon size={16}/> Aucun logo</span>}
              </div>
              <div className="flex flex-col gap-2">
                <input ref={logoInputRef} type="file" accept="image/png,image/jpeg" onChange={onLogoFile} className="hidden" data-testid="logo-input" />
                <button onClick={()=>logoInputRef.current?.click()} className="btn-primary text-sm" data-testid="logo-upload-btn"><Upload size={14}/> Choisir un fichier…</button>
                {logoPreview && <button onClick={resetLogo} className="text-xs text-bordeaux-700 hover:underline" data-testid="logo-reset-btn">Restaurer le logo par défaut</button>}
              </div>
            </div>
          </div>

          {/* Paramètres entreprise */}
          <div className="bg-white p-6 rounded-rama-md border border-black/10">
            <h2 className="font-display text-xl mb-4">Paramètres entreprise</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
              {[
                ["denomination","Dénomination"],["forme","Forme juridique"],["capital","Capital"],
                ["adresse","Adresse"],["siren","SIREN"],["siret","SIRET"],["rcs","RCS"],["tva_intra","TVA intra"],
                ["email","Email"],["telephone","Téléphone"],["site","Site"],
                ["iban","IBAN"],["bic","BIC"],["banque","Banque"],
                ["representant","Représentant"],["penalites","Pénalités"],["indemnite_recouvrement","Indemnité recouvrement"],
              ].map(([k,l])=>(
                <label key={k} className="block"><span className="text-xs text-ink-soft">{l}</span>
                  <input className="w-full p-2 border rounded mt-1" value={settings[k]||""} onChange={e=>setSettings({...settings, [k]:e.target.value})} />
                </label>
              ))}
            </div>
            <button onClick={async()=>{ try{ await api.put("/docs/settings", settings); toast.success("Enregistré"); } catch { toast.error("Erreur"); } }} className="btn-primary mt-4">Enregistrer</button>
          </div>
        </div>
      )}

      {/* ============ CLIENT MODAL ============ */}
      {clientForm && (
        <div className="fixed inset-0 z-50 bg-anthracite/70 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white max-w-lg w-full mt-16 rounded-rama-md p-6">
            <h3 className="font-display text-xl mb-4">{clientForm.id?"Modifier":"Nouveau"} client</h3>
            <div className="space-y-3 text-sm">
              <select className="w-full p-2 border rounded" value={clientForm.type} onChange={e=>setClientForm({...clientForm, type:e.target.value})}>
                <option value="particulier">Particulier</option><option value="professionnel">Professionnel</option>
              </select>
              {clientForm.type==="professionnel" && <input className="w-full p-2 border rounded" placeholder="Raison sociale" value={clientForm.raison_sociale||""} onChange={e=>setClientForm({...clientForm, raison_sociale:e.target.value})} />}
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="client-nom" className="p-2 border rounded" placeholder="Nom" value={clientForm.nom||""} onChange={e=>setClientForm({...clientForm, nom:e.target.value})} />
                <input data-testid="client-prenom" className="p-2 border rounded" placeholder="Prénom" value={clientForm.prenom||""} onChange={e=>setClientForm({...clientForm, prenom:e.target.value})} />
              </div>
              <input data-testid="client-email" className="w-full p-2 border rounded" placeholder="Email" value={clientForm.email||""} onChange={e=>setClientForm({...clientForm, email:e.target.value})} />
              <input className="w-full p-2 border rounded" placeholder="Téléphone" value={clientForm.telephone||""} onChange={e=>setClientForm({...clientForm, telephone:e.target.value})} />
              <input className="w-full p-2 border rounded" placeholder="Adresse" value={clientForm.adresse||""} onChange={e=>setClientForm({...clientForm, adresse:e.target.value})} />
              <div className="grid grid-cols-2 gap-2">
                <input className="p-2 border rounded" placeholder="CP" value={clientForm.cp||""} onChange={e=>setClientForm({...clientForm, cp:e.target.value})} />
                <input className="p-2 border rounded" placeholder="Ville" value={clientForm.ville||""} onChange={e=>setClientForm({...clientForm, ville:e.target.value})} />
              </div>
              {clientForm.type==="professionnel" && <div className="grid grid-cols-2 gap-2">
                <input className="p-2 border rounded" placeholder="SIREN" value={clientForm.siren||""} onChange={e=>setClientForm({...clientForm, siren:e.target.value})} />
                <input className="p-2 border rounded" placeholder="SIRET" value={clientForm.siret||""} onChange={e=>setClientForm({...clientForm, siret:e.target.value})} />
              </div>}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button data-testid="client-cancel" onClick={()=>setClientForm(null)} className="btn-ghost">Annuler</button>
              <button data-testid="client-save" onClick={saveClient} className="btn-primary">Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {signing && editing?.id && (
        <SignaturePadModal
          defaultName={clientLabelById(editing.client_id)}
          onClose={()=>setSigning(false)}
          onSave={submitSignature}
        />
      )}
      {sending && <SendEmailModal docType={sending.docType} doc={sending.doc} defaultTo={clients.find(c => c.id === sending.doc.client_id)?.email || ""} onClose={() => setSending(null)} onSent={load} />}
    </div>
  );
}
